import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pathname } from 'Routes';
import {
  overlay, center, card, heading, subheading, body,
  primaryBtn, secondaryBtn, ctaRow, isMobileQuery,
} from './styles';

// Post-avatar opt-in screen for the interview-record feature.
//
// Routing contract:
//   • Avatar sends new users here with location.state.nextPath = the
//     destination they'd have gone to otherwise (e.g. /welcome,
//     /quick-user-welcome). If state is missing we fall back to /profile.
//   • Yes → /interview/host (starts the recorder flow).
//   • Skip → navigate to nextPath. State is consumed; if the user comes
//     back to /interview/intro directly via URL they just see the page
//     again with the safe fallback destination.
//
// This page does NOT touch the camera or signaling — that lives on
// /interview/host. Keeping the opt-in cheap means users who skip don't
// pay any permission-prompt cost.
export const InterviewIntro = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = (location.state && location.state.nextPath) || Pathname.profile.basePath;

  const [isMobile, setIsMobile] = useState(isMobileQuery);
  useEffect(() => {
    const onResize = () => setIsMobile(isMobileQuery());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleYes = () => {
    navigate(Pathname.interview.host, { state: { nextPath } });
  };
  const handleSkip = () => {
    navigate(nextPath, { replace: true });
  };

  return (
    <div style={overlay}>
      <div style={center(isMobile)}>
        <div style={card(isMobile)}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '2.4rem' : '3rem', lineHeight: 1, marginBottom: '10px' }} aria-hidden="true">
              🎙️
            </div>
            <h1 style={heading(isMobile)}>Record a conversation</h1>
            <p style={subheading}>
              Invite someone and have a real talk — we&apos;ll record it for your Projector.
            </p>
            <p style={body}>
              Send them a link, they join from any phone or laptop, and you record the whole
              thing right in the browser. No installs, no scheduling — just hit start when
              they show up.
            </p>
          </div>

          {/* Visual cue — a stylized two-tile preview so users see what they're agreeing
              to. Pure decoration; the real call UI lives on /interview/host. */}
          <div
            aria-hidden="true"
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '12px',
              padding: isMobile ? '12px' : '20px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '16px',
            }}
          >
            {['You', 'Your guest'].map((label) => (
              <div
                key={label}
                style={{
                  aspectRatio: '16 / 9',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, rgba(90,165,239,0.18), rgba(255,209,102,0.12))',
                  border: '1px solid rgba(255,255,255,0.10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#cfd1da',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                {label}
              </div>
            ))}
          </div>

          <div style={ctaRow}>
            <button type="button" onClick={handleSkip} style={secondaryBtn}>
              Not now
            </button>
            <button type="button" onClick={handleYes} style={primaryBtn(false)}>
              Yes, let&apos;s record
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewIntro;
